import {
  Target,
  TrendUp,
  ChartLineUp,
  Trophy,
  Percent,
  WarningCircle,
} from "@phosphor-icons/react";
import { getCardClass, getTextPrimary, getTextSecondary } from "../../lib/styleHelpers";
import { PerformanceChart } from "./PerformanceChart";
import { PerformanceTable } from "./PerformanceTable";

function MetricBox({ icon: Icon, label, value, color, isDark, testId }) {
  return (
    <div className={`p-4 rounded-xl border ${getCardClass(isDark)}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={18} className={getTextSecondary(isDark)} />
        <span className={`text-xs font-body ${getTextSecondary(isDark)}`}>{label}</span>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

function calcWinRate(wins, total) {
  if (total === 0) return 0;
  return Math.round((wins / total) * 100);
}

export function PerformanceTab({ performance, isDark }) {
  if (!performance || performance.total_closed === 0) {
    return (
      <div className="animate-fade-in" data-testid="performance-content">
        <div className={`rounded-xl p-12 text-center border ${getCardClass(isDark)}`}>
          <Target size={48} className={`mx-auto mb-4 ${isDark ? "text-gray-600" : "text-gray-300"}`} />
          <h3 className={`text-lg font-bold mb-2 ${getTextPrimary(isDark)}`}>No Closed Positions Yet</h3>
          <p className={`font-body text-sm ${getTextSecondary(isDark)}`}>
            Close portfolio positions to see predicted vs actual performance comparison
          </p>
        </div>
      </div>
    );
  }

  const deviationColor = performance.avg_deviation <= 10 ? "text-emerald-500" : "text-amber-500";
  const actualColor = performance.avg_actual_roi >= 0 ? "text-emerald-500" : "text-red-500";
  const winRate = calcWinRate(performance.winning_predictions, performance.total_closed);

  const chartData = performance.comparisons.map((c) => ({
    name: c.title.length > 18 ? c.title.substring(0, 18) + "..." : c.title,
    predicted: c.predicted_roi,
    actual: c.actual_roi,
  }));

  return (
    <div className="animate-fade-in space-y-6" data-testid="performance-content">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricBox icon={Target} label="Prediction Accuracy" value={`${performance.prediction_accuracy}%`} color="text-purple-500" isDark={isDark} testId="perf-accuracy" />
        <MetricBox icon={TrendUp} label="Avg Predicted ROI" value={`${performance.avg_predicted_roi}%`} color={getTextPrimary(isDark)} isDark={isDark} testId="perf-predicted" />
        <MetricBox icon={ChartLineUp} label="Avg Actual ROI" value={`${performance.avg_actual_roi}%`} color={actualColor} isDark={isDark} testId="perf-actual" />
        <MetricBox icon={Percent} label="Avg Deviation" value={`${performance.avg_deviation}%`} color={deviationColor} isDark={isDark} testId="perf-deviation" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricBox icon={Trophy} label="Winning Trades" value={performance.winning_predictions} color="text-emerald-500" isDark={isDark} testId="perf-wins" />
        <MetricBox icon={WarningCircle} label="Losing Trades" value={performance.losing_predictions} color="text-red-500" isDark={isDark} testId="perf-losses" />
        <MetricBox icon={Target} label="Total Closed" value={performance.total_closed} color={getTextPrimary(isDark)} isDark={isDark} testId="perf-total" />
        <MetricBox icon={Trophy} label="Win Rate" value={`${winRate}%`} color="text-purple-500" isDark={isDark} testId="perf-winrate" />
      </div>

      <PerformanceChart chartData={chartData} isDark={isDark} />
      <PerformanceTable comparisons={performance.comparisons} isDark={isDark} />
    </div>
  );
}
